/**
 * Fachregeln der Kooperationen, die Übersicht, Kennzahlen, Wächter und Kooperationsseiten gleich rechnen (Nachprüfung 0060):
 *   Staffel          gestaffelte Provision je Abrechnungsmonat (Grenzstufen wie bei der Einkommensteuer: 20 % bis 5.000 €, darüber 15 %)
 *   Umsatzbeginn     ab welchem Arbeitsstand oder mit welchem Vertrag eine Kooperation als Umsatz zählt (Einstellung der Agentur)
 *   Monatsausgang    Storno, Ausfallhonorar, „nicht abgenommen“ mit Minderung oder Ausfallhonorar in Prozent
 *   Abzug der Marke  Agenturvergütung der Mediaagentur der Marke (AE-Provision, Agenturrabatt), trägt die Aufteilung oder die Agentur
 *   Exklusivität     Konflikte zwischen Kooperationen desselben Creators im selben Zeitraum
 *   Warenwert        Sachleistungen je Creator und Jahr (Grundlage der Versteuerung beim Creator)
 * Rein und ohne Datenbank; Beträge in Cent, kaufmännisch gerundet.
 */
import type { Cents, MonthKey } from "./types.js";

// ── Staffel ──────────────────────────────────────────────────────────────────

/** Eine Stufe: bis einschließlich uptoCents (null = ohne Obergrenze) gilt percent. */
export interface CommissionTier {
  uptoCents: Cents | null;
  percent: number;
}

/** Aus der Datenbank (deals.commission_tiers, talents.commission_tiers): [{ "upto": 500000, "bp": 2000 }, { "upto": null, "bp": 1500 }]. */
export function tiersFromJson(v: unknown): CommissionTier[] | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const out: CommissionTier[] = [];
  for (const raw of v) {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as { upto?: unknown; bp?: unknown };
    const bp = typeof r.bp === "number" ? r.bp : Number(r.bp);
    const upto = r.upto == null ? null : typeof r.upto === "number" ? r.upto : Number(r.upto);
    if (!Number.isFinite(bp) || (upto !== null && !Number.isFinite(upto))) return null;
    out.push({ uptoCents: upto, percent: bp / 100 });
  }
  return validTiers(out) ? out : null;
}

export function tiersToJson(tiers: readonly CommissionTier[] | null): { upto: number | null; bp: number }[] | null {
  if (!tiers?.length) return null;
  return tiers.map((t) => ({ upto: t.uptoCents, bp: Math.round(t.percent * 100) }));
}

/** Stufen aufsteigend, jede Grenze größer als die vorige, nur die letzte offen, Sätze zwischen 0 und 100 %. */
export function validTiers(tiers: readonly CommissionTier[]): boolean {
  if (!tiers.length || tiers.length > 10) return false;
  let prev = 0;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i]!;
    if (!(Number.isFinite(t.percent) && t.percent >= 0 && t.percent <= 100)) return false;
    const last = i === tiers.length - 1;
    if (last) {
      if (t.uptoCents !== null) return false;
    } else if (t.uptoCents === null || !Number.isInteger(t.uptoCents) || t.uptoCents <= prev) return false;
    else prev = t.uptoCents;
  }
  return true;
}

/** Provision nach Staffel auf einen Betrag (Grenzstufen). Negative Beträge (Gutschrift) mit dem Satz der ersten Stufe. */
export function tieredCommissionCents(baseCents: Cents, tiers: readonly CommissionTier[]): Cents {
  if (baseCents <= 0) return Math.round((baseCents * (tiers[0]?.percent ?? 0)) / 100);
  let prev = 0;
  let sum = 0;
  for (const t of tiers) {
    const cap = t.uptoCents ?? Number.POSITIVE_INFINITY;
    const portion = Math.min(baseCents, cap) - prev;
    if (portion > 0) sum += (portion * t.percent) / 100;
    if (baseCents <= cap) break;
    prev = cap;
  }
  return Math.round(sum);
}

/** Provision auf einen Betrag: Staffel, sonst fester Satz. */
export function commissionCents(baseCents: Cents, rate: { percent: number; tiers?: readonly CommissionTier[] | null }): Cents {
  return rate.tiers?.length ? tieredCommissionCents(baseCents, rate.tiers) : Math.round((baseCents * rate.percent) / 100);
}

/** Durchschnittssatz der Staffel auf diesen Betrag (für Anzeigen und Wege, die einen Satz erwarten); ohne Betrag der Satz der ersten Stufe. */
export function effectiveCommissionPercent(baseCents: Cents, rate: { percent: number; tiers?: readonly CommissionTier[] | null }): number {
  if (!rate.tiers?.length) return rate.percent;
  if (baseCents <= 0) return rate.tiers[0]!.percent;
  return (tieredCommissionCents(baseCents, rate.tiers) / baseCents) * 100;
}

/** „20 % bis 5.000 €, darüber 15 %“ */
export function tiersText(tiers: readonly CommissionTier[], fmt: (cents: Cents) => string, pct: (v: number) => string): string {
  return tiers.map((t, i) => (t.uptoCents === null ? (i === 0 ? pct(t.percent) : `darüber ${pct(t.percent)}`) : `${pct(t.percent)} bis ${fmt(t.uptoCents)}`)).join(", ");
}

// ── Umsatzbeginn ─────────────────────────────────────────────────────────────

/** Arbeitsstände vor dem Umsatzbeginn (KZ-10: ab dem Stand nach „Vertrag zur Unterschrift“). */
export const REVENUE_PENDING_STATUSES: ReadonlySet<string> = new Set(["planning", "contract"]);

/** Einstellung „Umsatzbeginn“ (org_settings.revenue_start, Produkt-Standard seit 0109: first_event). */
export const REVENUE_START_MODES = ["first_event", "status", "contract_signed"] as const;
export type RevenueStartMode = (typeof REVENUE_START_MODES)[number];

/**
 * Zählt die Kooperation schon als Umsatz? Einstellung der Agentur:
 *   first_event      (Standard neuer Agenturen, Probelauf M2): was zuerst eintritt – Arbeitsstand nach „Vertrag zur
 *                    Unterschrift“, unterschriebener Vertrag oder die erste abgerechnete bzw. veröffentlichte Monatsleistung
 *                    (`activity`: festgeschriebene Rechnung zur Kooperation oder ein Beitrag „live“)
 *   status           nur nach Arbeitsstand (bis 0109 Standard)
 *   contract_signed  erst mit unterschriebenem Vertrag
 * Blockiert und storniert regelt der Rechenkern gesondert (nur Verbuchtes bzw. Ausfallhonorar).
 * Eine Regel für Übersicht, Kennzahlen, Wächter, Monatsdetails und die Kooperationsseite.
 */
export function dealRevenueStarted(status: string, opts: { mode?: string | null; signed?: boolean; activity?: boolean } = {}): boolean {
  if (opts.mode === "contract_signed") return !!opts.signed;
  if (opts.mode === "first_event") return !REVENUE_PENDING_STATUSES.has(status) || !!opts.signed || !!opts.activity;
  // Nach Arbeitsstand (Vorgabe): ab dem Stand nach „Vertrag zur Unterschrift“, oder sobald etwas festgeschrieben abgerechnet ist.
  // Eine Rechnung ist Umsatz, auch wenn niemand den Arbeitsstand weitergestellt hat (Praxistest K-148).
  return !REVENUE_PENDING_STATUSES.has(status) || !!opts.activity;
}

/**
 * Erste abgerechnete oder veröffentlichte Monatsleistung einer Kooperation (für first_event): eine festgeschriebene Rechnung
 * (freigegeben, versendet oder bezahlt, ohne Stornorechnungen) oder ein Beitrag „live“.
 */
export function dealRevenueActivity(dealId: string, data: {
  invoices?: readonly { deal_id?: string | null; status?: string | null; credit_for?: string | null }[];
  deliverables?: readonly { deal_id?: string | null; status?: string | null }[];
}): boolean {
  return (data.invoices ?? []).some((i) => i.deal_id === dealId && !i.credit_for && ["approved", "sent", "paid"].includes(String(i.status)))
    || (data.deliverables ?? []).some((x) => x.deal_id === dealId && x.status === "live");
}

// ── Monatsausgang ────────────────────────────────────────────────────────────

/**
 * Wie ein Monat endet, wenn er nicht wie vereinbart geleistet wird:
 *   cancelled             storniert ohne Honorar: zählt nie, wird nicht abgerechnet
 *   cancellation_fee      storniert mit Ausfallhonorar: der Monat bleibt mit diesem Betrag abrechenbar
 *   not_accepted_reduced  Marke nimmt nicht ab, Honorar gemindert (Betrag)
 *   not_accepted_fee      Marke nimmt nicht ab, Ausfallhonorar in Prozent des vereinbarten Honorars
 * Der ursprüngliche Betrag bleibt erhalten (original_amount_cents), „Wieder öffnen“ stellt ihn her.
 */
export type MonthOutcome = "cancelled" | "cancellation_fee" | "not_accepted_reduced" | "not_accepted_fee";
export const MONTH_OUTCOME_LABEL: Readonly<Record<MonthOutcome, string>> = {
  cancelled: "Storniert",
  cancellation_fee: "Storniert mit Ausfallhonorar",
  not_accepted_reduced: "Nicht abgenommen · Honorar gemindert",
  not_accepted_fee: "Nicht abgenommen · Ausfallhonorar",
};
/** Kurzbezeichnung der Leistungszeile auf Anforderung und Rechnung */
export const MONTH_OUTCOME_LINE: Readonly<Record<Exclude<MonthOutcome, "cancelled">, string>> = {
  cancellation_fee: "Ausfallhonorar",
  not_accepted_reduced: "Honorar nach Minderung",
  not_accepted_fee: "Ausfallhonorar",
};

/** Betrag eines Ausgangs aus Prozent des ursprünglichen Honorars oder als Festbetrag. */
export function outcomeAmountCents(input: { originalCents: Cents; percent?: number | null; cents?: Cents | null }): Cents | null {
  if (input.cents != null) return input.cents;
  if (input.percent != null && Number.isFinite(input.percent)) return Math.round((input.originalCents * input.percent) / 100);
  return null;
}

/** Ausfallhonorar oder Minderung über dem vereinbarten Honorar: vor dem Speichern nachfragen. */
export function outcomeAboveOriginal(amountCents: Cents, originalCents: Cents | null): boolean {
  return originalCents != null && amountCents > originalCents;
}

/** Eine Kooperation stornieren: Ausfallhonorar in Prozent auf jeden offenen Monat (Anteil an seinem Honorar). */
export function dealCancellationPlan(months: readonly { id: string; amountCents: Cents | null; open: boolean }[], percent: number | null): {
  id: string; outcome: "cancelled" | "cancellation_fee"; feeCents: Cents | null;
}[] {
  return months.filter((m) => m.open).map((m) => {
    const fee = percent != null && percent > 0 ? Math.round(((m.amountCents ?? 0) * percent) / 100) : 0;
    return fee > 0 ? { id: m.id, outcome: "cancellation_fee" as const, feeCents: fee } : { id: m.id, outcome: "cancelled" as const, feeCents: null };
  });
}

// ── Agenturvergütung der Marke (AE-Provision, Agenturrabatt) ────────────────

/** Wer den Abzug trägt: shared = Basis der Aufteilung ist der Betrag nach Abzug; agency = der Creator-Anteil bleibt, die Agentur trägt ihn. */
export type DiscountBearer = "shared" | "agency";
export interface BrandDiscount {
  percent: number;
  bearer: DiscountBearer;
}

export function brandDiscountFromRow(row: { brand_discount_bp?: number | null; brand_discount_bearer?: string | null } | null | undefined): BrandDiscount | null {
  if (!row || row.brand_discount_bp == null || row.brand_discount_bp <= 0) return null;
  return { percent: row.brand_discount_bp / 100, bearer: row.brand_discount_bearer === "agency" ? "agency" : "shared" };
}

/** Abzug auf das Honorar (Monatsbetrag und provisionspflichtige Zusatzpositionen; nicht auf Auslagen und Agenturgebühr). */
export function brandDiscountCents(honorarCents: Cents, discount: BrandDiscount | null | undefined): Cents {
  return discount ? Math.round((honorarCents * discount.percent) / 100) : 0;
}

/**
 * Anteil von Agentur und Creator am Honorar eines Monats nach Satz oder Staffel und nach dem Abzug der Marke.
 * Summenprobe: creator + agency = honorar − Abzug (die Marke zahlt den Betrag nach Abzug).
 */
export function shareOfHonorar(honorarCents: Cents, rate: { percent: number; tiers?: readonly CommissionTier[] | null }, discount?: BrandDiscount | null): {
  agencyCents: Cents; creatorCents: Cents; discountCents: Cents;
} {
  const d = brandDiscountCents(honorarCents, discount);
  if (discount?.bearer === "agency") {
    const agency = commissionCents(honorarCents, rate) - d;
    return { agencyCents: agency, creatorCents: honorarCents - d - agency, discountCents: d };
  }
  const net = honorarCents - d;
  const agency = commissionCents(net, rate);
  return { agencyCents: agency, creatorCents: net - agency, discountCents: d };
}

/**
 * Erwarteter Betrag der Rechnung des Creators an die Agentur: sein Anteil am Honorar plus durchlaufende Posten (Auslagen).
 * Für den Wächter (Betrag gegen die Vereinbarung) und die Vorschläge der Anforderung.
 */
export function expectedCreatorNetCents(input: { honorarCents: Cents; passThroughCents: Cents; rate: { percent: number; tiers?: readonly CommissionTier[] | null }; discount?: BrandDiscount | null }): Cents {
  return shareOfHonorar(input.honorarCents, input.rate, input.discount).creatorCents + input.passThroughCents;
}

/**
 * Anteil des Creators an einem Monat als Positionen (Rechnung des Creators an die Agentur, Gutschrift, Vorschläge der Anforderung):
 * die provisionspflichtigen Positionen tragen zusammen genau den Creator-Anteil nach Satz oder Staffel und nach dem Abzug der
 * Marke (shareOfHonorar), anteilig nach ihrem Betrag verteilt; durchlaufende Posten (Auslagen) bleiben voll. Eine Rechenregel
 * für Aufteilung, Wächter, Anforderung, Gutschrift und Creator-Portal (Korrektur 0075).
 */
export function creatorShareLines<L extends { amountCents: Cents; commissionFree: boolean }>(lines: readonly L[], rate: { percent: number; tiers?: readonly CommissionTier[] | null },
  discount?: BrandDiscount | null): (L & { amountCents: Cents })[] {
  const honorar = lines.filter((l) => !l.commissionFree).reduce((a, l) => a + l.amountCents, 0);
  return scaleCommissionable(lines, shareOfHonorar(honorar, rate, discount).creatorCents);
}

/**
 * Was die Marke für die Positionen eines Monats zahlt, wenn der Creator direkt an sie abrechnet: Honorar nach ihrem Abzug,
 * durchlaufende Posten voll. Anteilig auf die provisionspflichtigen Positionen verteilt (keine Minuszeile auf der Creator-Rechnung).
 */
export function brandNetLines<L extends { amountCents: Cents; commissionFree: boolean }>(lines: readonly L[], discount?: BrandDiscount | null): (L & { amountCents: Cents })[] {
  const honorar = lines.filter((l) => !l.commissionFree).reduce((a, l) => a + l.amountCents, 0);
  return scaleCommissionable(lines, honorar - brandDiscountCents(honorar, discount));
}

/** Verteilt targetCents anteilig auf die provisionspflichtigen Positionen, Summe exakt (Rundungsrest an die größte); provisionsfreie bleiben. */
function scaleCommissionable<L extends { amountCents: Cents; commissionFree: boolean }>(lines: readonly L[], targetCents: Cents): (L & { amountCents: Cents })[] {
  const idx = lines.map((l, i) => ({ l, i })).filter((x) => !x.l.commissionFree && x.l.amountCents !== 0);
  const honorar = idx.reduce((a, x) => a + x.l.amountCents, 0);
  if (!idx.length || honorar === 0) return lines.map((l) => ({ ...l }));
  const byIndex = new Map(idx.map((x) => [x.i, Math.round((x.l.amountCents * targetCents) / honorar)]));
  const biggest = idx.reduce((a, x) => (Math.abs(x.l.amountCents) > Math.abs(a.l.amountCents) ? x : a)).i;
  byIndex.set(biggest, byIndex.get(biggest)! + targetCents - [...byIndex.values()].reduce((a, c) => a + c, 0));
  return lines.map((l, i) => (byIndex.has(i) ? { ...l, amountCents: byIndex.get(i)! } : { ...l }));
}

// ── Zusatzpositionen je Monat ────────────────────────────────────────────────

/** Erster Monat einer Kooperation (nach Monat sortiert); dorthin gehören Positionen ohne Monat. */
export function firstPeriodId(periods: readonly { id: string; month: string }[]): string | null {
  let first: { id: string; month: string } | null = null;
  for (const p of periods) if (!first || p.month < first.month) first = p;
  return first?.id ?? null;
}

/**
 * Gehört eine Zusatzposition zu diesem Monat? Positionen mit Monat zu genau diesem, Positionen ohne Monat („Ganze Kooperation“)
 * oder mit einem fremden Monat nur zum ersten Monat der Kooperation – wie Aufteilung, Übersicht und Anforderung. Sonst stünden
 * sie auf jeder Monatsrechnung an die Marke (GELD-2) oder fehlten im Sollbetrag des Wächters (GELD-8).
 * periods: die Monate derselben Kooperation.
 */
export function extraInPeriod(extraPeriodId: string | null | undefined, periodId: string, periods: readonly { id: string; month: string }[]): boolean {
  if (extraPeriodId === periodId) return true;
  if (extraPeriodId && periods.some((p) => p.id === extraPeriodId)) return false;
  return firstPeriodId(periods) === periodId;
}

// ── Exklusivität ─────────────────────────────────────────────────────────────

/**
 * Branchen für Marken und Exklusivitäten (J-01, 0107): Auswahlliste plus eigene Einträge. Gespeichert wird der Schlüssel
 * (brands.industry, deals.exclusive_industry) oder der eigene Text; verglichen wird ohne Groß-/Kleinschreibung.
 */
export const BRAND_INDUSTRIES: readonly { key: string; de: string; en: string }[] = [
  { key: "beauty", de: "Beauty & Kosmetik", en: "Beauty & cosmetics" },
  { key: "fashion", de: "Mode & Accessoires", en: "Fashion & accessories" },
  { key: "fitness", de: "Fitness & Sport", en: "Fitness & sports" },
  { key: "nutrition", de: "Ernährung & Nahrungsergänzung", en: "Nutrition & supplements" },
  { key: "food", de: "Lebensmittel & Getränke", en: "Food & beverages" },
  { key: "travel", de: "Reisen & Tourismus", en: "Travel & tourism" },
  { key: "tech", de: "Technik & Elektronik", en: "Tech & electronics" },
  { key: "gaming", de: "Gaming", en: "Gaming" },
  { key: "finance", de: "Finanzen & Versicherungen", en: "Finance & insurance" },
  { key: "automotive", de: "Auto & Mobilität", en: "Automotive & mobility" },
  { key: "home", de: "Wohnen & Einrichtung", en: "Home & interior" },
  { key: "family", de: "Familie & Kinder", en: "Family & kids" },
  { key: "pets", de: "Haustiere", en: "Pets" },
  { key: "health", de: "Gesundheit & Pharma", en: "Health & pharma" },
  { key: "retail", de: "Handel & E-Commerce", en: "Retail & e-commerce" },
  { key: "media", de: "Medien & Unterhaltung", en: "Media & entertainment" },
  { key: "telecom", de: "Telekommunikation", en: "Telecommunications" },
  { key: "sustainability", de: "Nachhaltigkeit", en: "Sustainability" },
];

/** Anzeigename einer Branche: bekannter Schlüssel in der Sprache, sonst der eigene Eintrag. */
export function industryLabel(value: string | null | undefined, lang: "de" | "en" = "de"): string {
  const v = (value ?? "").trim();
  const known = BRAND_INDUSTRIES.find((x) => x.key === v.toLowerCase());
  return known ? known[lang] : v;
}

/** Branche zum Vergleich: Schlüssel oder eigener Text, getrimmt und klein; bekannte Namen werden zum Schlüssel. */
export function industryKey(value: string | null | undefined): string {
  const v = (value ?? "").trim().toLowerCase();
  if (!v) return "";
  const byName = BRAND_INDUSTRIES.find((x) => x.de.toLowerCase() === v || x.en.toLowerCase() === v);
  return byName ? byName.key : v;
}

export interface ExclusivityDeal {
  id: string;
  talentId: string;
  brandId: string;
  title: string;
  status: string;
  archived?: boolean;
  exclusive: boolean;
  exclusiveUntil: string | null;
  exclusiveScope: string | null;
  /** Branche der Exklusivität (deals.exclusive_industry, 0107); ohne Angabe gilt jede andere Marke als Rückfrage */
  exclusiveIndustry?: string | null;
  /** Leistungszeitraum: erster Tag und letzter Tag (YYYY-MM-DD) */
  from: string;
  to: string;
}

export interface ExclusivityConflict {
  /** Die exklusive Kooperation, die den Konflikt auslöst */
  exclusiveDealId: string;
  /** Die andere Kooperation im selben Zeitraum */
  otherDealId: string;
  talentId: string;
  until: string | null;
  scope: string | null;
  /** Der Name der anderen Marke steht ausdrücklich in der Branchenangabe */
  named: boolean;
  /** Die andere Marke hat dieselbe Branche wie die Exklusivität (0107) */
  sameIndustry?: boolean;
  /** Die Exklusivität nennt eine Branche, die andere Marke hat aber keine: bitte prüfen (0107) */
  industryUnknown?: boolean;
}

/** Zeitraum der Exklusivität: vom Beginn der Kooperation bis zum vereinbarten Ende, sonst bis zum Ende der Leistung. */
export function exclusivityWindow(d: Pick<ExclusivityDeal, "from" | "to" | "exclusiveUntil">): { from: string; to: string } {
  return { from: d.from, to: d.exclusiveUntil ?? d.to };
}

const ACTIVE = (d: ExclusivityDeal) => !d.archived && d.status !== "cancelled";

/**
 * Konflikte eines Creators: eine exklusive Kooperation und eine Kooperation mit einer anderen Marke, deren Zeiträume sich
 * überschneiden. Ist die Marke im Beschreibungstext der Exklusivität ausdrücklich genannt, gilt der Konflikt als sicher.
 * J-01 (0107): Nennt die Exklusivität eine Branche (exclusiveIndustry) und kennt `brandIndustry` die Branche der anderen
 * Marke, gibt es eine Rückfrage nur bei gleicher Branche oder genannter Marke; Marken ohne Branche bleiben eine Rückfrage
 * (industryUnknown). Ohne Branche an der Exklusivität fragt die Prüfung wie bisher bei jeder anderen Marke.
 */
export function exclusivityConflicts(deals: readonly ExclusivityDeal[], brandName: (id: string) => string, only?: string,
  brandIndustry?: (id: string) => string | null | undefined): ExclusivityConflict[] {
  const out: ExclusivityConflict[] = [];
  const list = deals.filter(ACTIVE);
  for (const ex of list) {
    if (!ex.exclusive) continue;
    const w = exclusivityWindow(ex);
    const exInd = industryKey(ex.exclusiveIndustry);
    for (const other of list) {
      if (other.id === ex.id || other.talentId !== ex.talentId || other.brandId === ex.brandId) continue;
      if (only && ex.id !== only && other.id !== only) continue;
      if (other.from > w.to || other.to < w.from) continue;
      const name = brandName(other.brandId).trim().toLowerCase();
      const named = !!name && !!ex.exclusiveScope && ex.exclusiveScope.toLowerCase().includes(name);
      const otherInd = brandIndustry ? industryKey(brandIndustry(other.brandId)) : "";
      if (exInd && otherInd && exInd !== otherInd && !named) continue;
      out.push({
        exclusiveDealId: ex.id, otherDealId: other.id, talentId: ex.talentId, until: ex.exclusiveUntil, scope: ex.exclusiveScope, named,
        ...(exInd ? { sameIndustry: !!otherInd && otherInd === exInd, industryUnknown: !otherInd } : {}),
      });
    }
  }
  return out;
}

// ── Warenwert je Creator und Jahr ───────────────────────────────────────────

export interface GoodsValueRow {
  talentId: string;
  dealId: string;
  month: MonthKey;
  currency: string;
  goodsValueCents: Cents;
  /** Die Marke versteuert pauschal (§ 37b EStG): beim Creator in der Regel nicht zu versteuern */
  flatTaxed: boolean;
}

export interface GoodsValueSummary {
  talentId: string;
  year: string;
  currency: string;
  totalCents: Cents;
  flatTaxedCents: Cents;
  /** Beim Creator zu versteuern (ohne Pauschalversteuerung der Marke) */
  taxableCents: Cents;
  deals: number;
  months: MonthKey[];
}

/** Summen je Creator, Jahr und Währung; stornierte Monate liefert der Aufrufer nicht mit. */
export function goodsValueByTalentYear(rows: readonly GoodsValueRow[]): GoodsValueSummary[] {
  const map = new Map<string, GoodsValueSummary & { dealSet: Set<string> }>();
  for (const r of rows) {
    if (!(r.goodsValueCents > 0)) continue;
    const year = r.month.slice(0, 4);
    const key = `${r.talentId}|${year}|${r.currency}`;
    let e = map.get(key);
    if (!e) map.set(key, (e = { talentId: r.talentId, year, currency: r.currency, totalCents: 0, flatTaxedCents: 0, taxableCents: 0, deals: 0, months: [], dealSet: new Set() }));
    e.totalCents += r.goodsValueCents;
    if (r.flatTaxed) e.flatTaxedCents += r.goodsValueCents;
    else e.taxableCents += r.goodsValueCents;
    e.dealSet.add(r.dealId);
    if (!e.months.includes(r.month)) e.months.push(r.month);
  }
  return [...map.values()]
    .map(({ dealSet, ...e }) => ({ ...e, deals: dealSet.size, months: [...e.months].sort() }))
    .sort((a, b) => b.year.localeCompare(a.year) || b.totalCents - a.totalCents);
}
